import React, { useState } from 'react';

const DemoTracePanel = ({ executionTrace }) => {
  const [expanded, setExpanded] = useState({});

  if (!executionTrace) {
    return (
      <div style={{
        padding: '10px 14px', background: '#1e293b', borderRadius: '6px',
        fontSize: '12px', color: '#475569', textAlign: 'center',
      }}>
        Execution trace will appear here after a query is answered.
      </div>
    );
  }

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const gates = executionTrace.gates || [];
  const nodeActivations = executionTrace.node_activations || [];
  const firedPerTick = executionTrace.fired_per_tick || [];
  const explanationChain = executionTrace.explanation_chain || [];
  const confidence = executionTrace.confidence_breakdown || executionTrace.confidence || null;
  const contextAssembly = executionTrace.context_assembly || null;

  const Section = ({ title, color, count, sectionKey, children }) => (
    <div style={{ marginBottom: '8px' }}>
      <button
        onClick={() => toggle(sectionKey)}
        style={{
          width: '100%', textAlign: 'left', padding: '8px 12px',
          background: expanded[sectionKey] ? '#1e293b' : '#0f172a',
          border: '1px solid #1e293b', borderRadius: '6px',
          color: color, fontWeight: 600, fontSize: '12px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span>{title} {count !== undefined && `(${count})`}</span>
        <span style={{ fontSize: '10px', color: '#475569' }}>{expanded[sectionKey] ? '▼' : '▶'}</span>
      </button>
      {expanded[sectionKey] && (
        <div style={{ padding: '8px', borderLeft: `2px solid ${color}`, marginLeft: '6px', marginTop: '4px' }}>
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      background: '#0f172a', borderRadius: '6px', border: '1px solid #1e293b',
      padding: '10px', fontSize: '11px',
    }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: '#f1f5f9', marginBottom: '10px' }}>
        🔍 SNN Execution Trace
      </div>

      {/* Gates */}
      {gates.length > 0 && (
        <Section title="Logic Gates" color="#3b82f6" count={gates.length} sectionKey="gates">
          {gates.map((g, i) => (
            <div key={i} style={{
              padding: '6px 8px', marginBottom: '4px', background: '#0f172a',
              borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: g.fired ? '#22c55e' : '#ef4444',
              }} />
              <span style={{ color: '#cbd5e1' }}>{g.gate_name || g.gate_id || `Gate ${i}`}</span>
              <span style={{ color: '#475569', marginLeft: 'auto', fontFamily: 'monospace', fontSize: '10px' }}>
                {g.required || 0} required • {(g.satisfaction_progress || 0).toFixed(0)}% satisfied
              </span>
            </div>
          ))}
        </Section>
      )}

      {/* Node Activations */}
      {nodeActivations.length > 0 && (
        <Section title="Node Activations" color="#8b5cf6" count={nodeActivations.length} sectionKey="nodes">
          {nodeActivations.slice(0, 15).map((n, i) => (
            <div key={i} style={{
              padding: '4px 8px', marginBottom: '3px', display: 'flex',
              justifyContent: 'space-between', color: '#cbd5e1',
            }}>
              <span style={{ maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {n.node_name || n.node_id}
              </span>
              <span style={{ fontFamily: 'monospace', color: '#a5b4fc' }}>
                {typeof n.score === 'number' ? n.score.toFixed(3) : n.activation || '—'}
              </span>
            </div>
          ))}
          {nodeActivations.length > 15 && (
            <div style={{ color: '#475569', padding: '4px 8px' }}>+{nodeActivations.length - 15} more</div>
          )}
        </Section>
      )}

      {/* Fired per tick */}
      {firedPerTick.length > 0 && (
        <Section title="Firing Timeline" color="#f59e0b" count={`${firedPerTick.length} ticks`} sectionKey="ticks">
          <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '40px' }}>
            {firedPerTick.map((count, i) => (
              <div key={i} style={{
                flex: 1, minWidth: '3px',
                height: `${Math.max((count / Math.max(...firedPerTick)) * 100, 5)}%`,
                background: count > 0 ? '#f59e0b' : '#1e293b',
                borderRadius: '1px',
              }} title={`Tick ${i}: ${count} fired`} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', marginTop: '4px', fontSize: '10px' }}>
            <span>Tick 0</span>
            <span>Tick {firedPerTick.length - 1}</span>
          </div>
        </Section>
      )}

      {/* Explanation Chain */}
      {explanationChain.length > 0 && (
        <Section title="Explanation Chain" color="#10b981" count={explanationChain.length} sectionKey="chain">
          {explanationChain.map((link, i) => (
            <div key={i} style={{
              padding: '4px 8px', marginBottom: '3px', color: '#cbd5e1',
              borderBottom: '1px solid #1e293b',
            }}>
              {link.description || link.text || JSON.stringify(link)}
            </div>
          ))}
        </Section>
      )}

      {/* Confidence Breakdown */}
      {confidence && (
        <Section title="Confidence Breakdown" color="#06b6d4" sectionKey="confidence">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
            {Object.entries(confidence).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 4px', color: '#94a3b8' }}>
                <span>{k.replace(/_/g, ' ')}</span>
                <span style={{ fontFamily: 'monospace', color: '#67e8f9' }}>
                  {typeof v === 'number' ? v.toFixed(3) : String(v)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Context Assembly */}
      {contextAssembly && (
        <Section title="Context Assembly" color="#ec4899" sectionKey="context">
          {contextAssembly.verbatim_nodes && (
            <div style={{ color: '#94a3b8' }}>
              {contextAssembly.verbatim_nodes.length} verbatim node(s) assembled
            </div>
          )}
          {contextAssembly.context_potency && (
            <div style={{ color: '#94a3b8', marginTop: '4px' }}>
              Context potency: <strong style={{ color: '#f9a8d4' }}>
                {contextAssembly.context_potency}
              </strong>
            </div>
          )}
        </Section>
      )}

      {/* Fallback: dump raw keys */}
      {gates.length === 0 && nodeActivations.length === 0 && explanationChain.length === 0 && !confidence && (
        <div style={{ color: '#475569' }}>
          Trace keys: {Object.keys(executionTrace).join(', ')}
        </div>
      )}
    </div>
  );
};

export default DemoTracePanel;
